import { useGetTopBottomPerformingIdeasQuery } from '../../../../../apis/statistics/statistics-api.api.ts'
import { PerformingIdea } from '../../../../../apis/statistics/statistics-api.def.ts'
import ReactECharts from 'echarts-for-react'
import { DashboardChartBox } from '../dashboard-chart-box.tsx'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../../redux/hooks.ts'
import { selectDashboardInput } from '../../../../../redux/slices/dashboard-input.slice.ts'
import dayjs from 'dayjs'
import echartsTheme from '../../../../../styles/echarts/echarts-theme.json'
import { selectThemeStyle } from '../../../../../features/setting/setting.slice.ts'

interface TopBottomPerformingIdeasMobileChartProps {
  bookId: string
}

export const TopBottomPerformingIdeasMobileChart: React.FC<
  TopBottomPerformingIdeasMobileChartProps
> = ({ bookId }) => {
  const { t } = useTranslation()
  const dashboardInput = useAppSelector(selectDashboardInput)
  const themeStyle = useAppSelector(selectThemeStyle)
  const { data } = useGetTopBottomPerformingIdeasQuery({
    bookId: bookId,
    startDate: dayjs(dashboardInput.dateRange[0]).format('YYYY-MM-DD'),
    endDate: dayjs(dashboardInput.dateRange[1]).format('YYYY-MM-DD'),
  })

  if (
    !data ||
    (data && data.data.topIdeas.length === 0) ||
    data.data.bottomIdeas.length === 0
  ) {
    return <></>
  }

  const pnlAllZero = (ideas: PerformingIdea[]) =>
    ideas.every((idea) => idea.totalPnl === 0)

  if (pnlAllZero(data.data.topIdeas) && pnlAllZero(data.data.bottomIdeas)) {
    return (
      <>
        <div>
          All ideas have 0 pnl, cannot create Top/Bottom Performing Ideas
        </div>
      </>
    )
  }

  const Legend = () => {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '20px',
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', marginRight: '20px' }}
        >
          <div
            style={{
              borderRadius: '4px',
              width: '30px',
              height: '15px',
              backgroundColor: '#67b365',
              marginRight: '5px',
            }}
          ></div>
          <span>Long</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              borderRadius: '4px',
              width: '30px',
              height: '15px',
              backgroundColor: '#f26466',
              marginRight: '5px',
            }}
          ></div>
          <span>Short</span>
        </div>
      </div>
    )
  }

  const roundToNearest10000 = (value: number) => {
    if (value > 10000) {
      return Math.round(value / 10000) * 10000
    } else {
      return Math.round(value)
    }
  }

  const topIdeas = [...data.data.topIdeas]
    .filter((idea) => idea.totalPnl > 0)
    .sort((a, b) => a.totalPnl - b.totalPnl)
    .map((idea) => ({ ...idea, totalPnl: idea.totalPnl / 1_000_000 }))

  const bottomIdeas = [...data.data.bottomIdeas]
    .filter((idea) => idea.totalPnl < 0)
    .sort((a, b) => b.totalPnl - a.totalPnl)
    .map((idea) => ({ ...idea, totalPnl: idea.totalPnl / 1_000_000 }))

  const bestIdeasOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: { data: number }[]) => {
        return `${params[0].data.toFixed(2)}M`
      },
    },
    grid: {
      bottom: 150,
    },
    xAxis: [
      {
        type: 'category',
        data: [...topIdeas.map((i) => i.bbgCompositeKey)],
        position: 'bottom',
        axisLabel: {
          color: themeStyle.sumText,
          rotate: 90,
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: '($M)',
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        min: 0,
        max: roundToNearest10000(
          Math.max(Math.max(...topIdeas.map((i) => i.totalPnl)) * 1.1, 1)
        ),
        axisLabel: {
          color: themeStyle.sumText,
        },
      },
    ],
    series: [
      {
        type: 'bar',
        label: {
          show: true,
          rotate: 45,
          position: 'top',
          formatter: function (params: { value: number }) {
            return `${params.value.toFixed(2)}M`
          },
          color: themeStyle.sumText,
        },
        itemStyle: {
          color: function (params: { dataIndex: number }) {
            const idea = topIdeas[params.dataIndex]
            return idea.direction === 'Long' ? '#67b365' : '#f26466'
          },
        },
        data: [...topIdeas.map((i) => i.totalPnl)],
      },
    ],
  }

  const worstIdeasOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: { data: number }[]) => {
        return `${params[0].data.toFixed(2)}M`
      },
    },
    grid: {
      top: 150,
    },
    xAxis: [
      {
        type: 'category',
        data: [...bottomIdeas.map((i) => i.bbgCompositeKey)],
        position: 'top',
        axisLabel: {
          color: themeStyle.sumText,
          rotate: 90,
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: '($M)',
        nameTextStyle: {
          color: themeStyle.sumText,
        },
        min: 0,
        max: roundToNearest10000(
          Math.max(Math.max(...bottomIdeas.map((i) => i.totalPnl)) * 1.1, -10)
        ),
        axisLabel: {
          color: themeStyle.sumText,
        },
      },
    ],
    series: [
      {
        type: 'bar',
        label: {
          show: true,
          rotate: 45,
          position: 'bottom',
          formatter: function (params: { value: number }) {
            return `${params.value.toFixed(2)}M`
          },
          color: themeStyle.sumText,
        },
        itemStyle: {
          color: function (params: { dataIndex: number }) {
            const idea = topIdeas[params.dataIndex]
            return idea.direction === 'Long' ? '#67b365' : '#f26466'
          },
        },
        data: [...bottomIdeas.map((i) => i.totalPnl)],
      },
    ],
  }

  return (
    <>
      <DashboardChartBox title={t('dashboard.top_performing_ideas')}>
        <ReactECharts
          option={bestIdeasOption}
          theme={echartsTheme.themeName}
          style={{ height: '400px' }}
        />
        <Legend />
      </DashboardChartBox>
      <DashboardChartBox title={t('dashboard.bottom_performing_ideas')}>
        <ReactECharts
          option={worstIdeasOption}
          theme={echartsTheme.themeName}
          style={{ height: '400px' }}
        />
        <Legend />
      </DashboardChartBox>
    </>
  )
}
